import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { 
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, 
  CButton, CSpinner, CCard, CCardHeader, CCardBody, CBadge 
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilPencil, cilTrash, cilPlus, cilTag } from "@coreui/icons";
import dayjs from "dayjs";
import { ResponsivePie } from "@nivo/pie";
import DowntimeModal, { DOWNTIME_CHANGED_EVENT, notifyDowntimeChanged } from "./DowntimeModal";

// Длинный простой вне смены классифицируется автоматически как санкционированный.
// В базу такие записи не пишутся: они строятся из ответа аналитики (idle_events с
// in_schedule=false), поэтому не дублируются при пересчете и пропадают сами, если смены
// поменяли. Ручная классификация того же простоя (перекрытие от половины) заменяет автоматическую.
export const AUTO_OUT_OF_SHIFT_REASON = "Санкционированный (вне смены)";
const AUTO_OVERLAP_SHARE = 0.5;

const overlapSec = (a, b) =>
  Math.max(0, Math.min(new Date(a.end_time), new Date(b.end_time)) - Math.max(new Date(a.start_time), new Date(b.start_time))) /
  1000;

const DowntimesTab = ({ deviceId, startDate, endDate, isStaff, autoIdleEvents }) => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalData, setModalData] = useState({ visible: false, data: null });

  const startIso = startDate ? new Date(startDate).toISOString() : null;
  const endIso = endDate ? new Date(endDate).toISOString() : null;

  const fetchIncidents = async () => {
    if (!startIso || !endIso) return;
    setLoading(true);
    try {
      const res = await axios.get("/api/downtime-incidents/", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        params: {
          device_id: deviceId,
          start_date: startIso,
          end_date: endIso
        }
      });
      setIncidents(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidents();
  }, [deviceId, startIso, endIso]); 

  // Классификацию сохранили в другом месте страницы (таблица длинных простоев) - перезагружаем
  const fetchRef = useRef(fetchIncidents);
  fetchRef.current = fetchIncidents;
  useEffect(() => {
    const onChanged = (e) => {
      const changed = e.detail?.deviceId;
      if (changed == null || String(changed) === String(deviceId)) fetchRef.current();
    };
    window.addEventListener(DOWNTIME_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(DOWNTIME_CHANGED_EVENT, onChanged);
  }, [deviceId]);

  // Простои вне смены - автоматически, если их не классифицировали вручную
  const autoIncidents = useMemo(() => {
    if (!autoIdleEvents) return [];
    return autoIdleEvents
      .filter((ev) => ev.in_schedule === false)
      .map((ev) => ({
        id: `auto_${ev.start}`,
        auto: true,
        reason: AUTO_OUT_OF_SHIFT_REASON,
        comment: "Классифицирован автоматически: простой вне смены",
        author_name: "авто",
        start_time: ev.start,
        end_time: ev.end,
      }))
      .filter((auto) => {
        const dur = overlapSec(auto, auto);
        return !incidents.some((inc) => dur > 0 && overlapSec(inc, auto) >= dur * AUTO_OVERLAP_SHARE);
      });
  }, [autoIdleEvents, incidents]);

  const allIncidents = useMemo(
    () => [...incidents, ...autoIncidents].sort((a, b) => new Date(b.start_time) - new Date(a.start_time)),
    [incidents, autoIncidents]
  );

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить запись о простое?")) return;
    try {
      await axios.delete(`/api/downtime-incidents/${id}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      fetchIncidents();
      notifyDowntimeChanged(deviceId);
    } catch (e) {
      console.error(e);
    }
  };

  // Агрегация данных для круговой диаграммы Nivo по строковому полю reason
  const pieData = useMemo(() => {
    const map = {};
    allIncidents.forEach(inc => {
      const durationMins = dayjs(inc.end_time).diff(dayjs(inc.start_time), 'minute');
      if (map[inc.reason]) {
        map[inc.reason] += durationMins;
      } else {
        map[inc.reason] = durationMins;
      }
    });

    return Object.keys(map).map(key => ({
      id: key,
      label: key,
      value: map[key]
    }));
  }, [allIncidents]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0 text-danger fw-bold">Журнал простоев</h5>
        {isStaff && (
          <CButton color="danger" className="text-white fw-bold shadow-sm" size="sm" onClick={() => setModalData({ visible: true, data: null })}>
            <CIcon icon={cilPlus} className="me-1"/> Зафиксировать простой
          </CButton>
        )}
      </div>

      {allIncidents.length > 0 && (
        <CCard className="mb-4 shadow-sm border-0 bg-white">
          <CCardBody style={{ height: "300px" }}>
            <h6 className="text-center text-muted fw-bold border-bottom pb-2">Структура простоев (в минутах)</h6>
            <ResponsivePie
              data={pieData}
              margin={{ top: 20, right: 80, bottom: 20, left: 80 }}
              innerRadius={0.4}
              padAngle={2}
              cornerRadius={4}
              colors={{ scheme: 'nivo' }} // Nivo сам раскрасит разные строковые причины
              borderWidth={1}
              borderColor={{ from: 'color', modifiers: [ [ 'darker', 0.2 ] ] }}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor="#333333"
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: 'color' }}
              arcLabelsSkipAngle={10}
              arcLabelsTextColor="#ffffff"
            />
          </CCardBody>
        </CCard>
      )}

      <CCard className="shadow-sm border-0">
        <CCardHeader className="bg-white fw-bold border-bottom">Детализация</CCardHeader>
        {loading ? (
          <div className="text-center p-4"><CSpinner color="danger"/></div>
        ) : (
          <div className="table-responsive">
            <CTable hover align="middle" small className="mb-0">
              <CTableHead color="light">
                <CTableRow>
                  <CTableHeaderCell>Начало</CTableHeaderCell>
                  <CTableHeaderCell>Конец</CTableHeaderCell>
                  <CTableHeaderCell>Длительность</CTableHeaderCell>
                  <CTableHeaderCell>Причина</CTableHeaderCell>
                  <CTableHeaderCell>Комментарий</CTableHeaderCell>
                  <CTableHeaderCell>Автор</CTableHeaderCell>
                  {isStaff && <CTableHeaderCell className="text-end">Действия</CTableHeaderCell>}
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {allIncidents.length === 0 ? (
                  <CTableRow><CTableDataCell colSpan={7} className="text-center text-muted py-4">Классифицированных простоев нет</CTableDataCell></CTableRow>
                ) : (
                  allIncidents.map((inc) => {
                    const durationMins = dayjs(inc.end_time).diff(dayjs(inc.start_time), 'minute');
                    return (
                      <CTableRow key={inc.id}>
                        <CTableDataCell className="fw-semibold text-muted">{dayjs(inc.start_time).format('DD.MM.YY HH:mm')}</CTableDataCell>
                        <CTableDataCell className="fw-semibold text-muted">{dayjs(inc.end_time).format('DD.MM.YY HH:mm')}</CTableDataCell>
                        <CTableDataCell><span className="text-danger fw-bold">{durationMins} мин</span></CTableDataCell>
                        <CTableDataCell>
                            <CBadge color={inc.auto ? "secondary" : "dark"} shape="rounded-pill">{inc.reason}</CBadge>
                            {inc.auto && (
                              <CBadge color="light" textColor="dark" shape="rounded-pill" className="ms-1 border">авто</CBadge>
                            )}
                        </CTableDataCell>
                        <CTableDataCell className="text-muted small" style={{ maxWidth: '250px', whiteSpace: 'normal' }}>{inc.comment || '-'}</CTableDataCell>
                        <CTableDataCell className="small text-muted fst-italic">{inc.author_name}</CTableDataCell>
                        {isStaff && inc.auto && (
                          <CTableDataCell className="text-end">
                            {/* автоматическую классификацию можно заменить ручной */}
                            <CButton
                              size="sm"
                              color="secondary"
                              variant="ghost"
                              title="Классифицировать вручную"
                              onClick={() => setModalData({ visible: true, data: { start_time: new Date(inc.start_time), end_time: new Date(inc.end_time) } })}
                            >
                              <CIcon icon={cilTag} />
                            </CButton>
                          </CTableDataCell>
                        )}
                        {isStaff && !inc.auto && (
                          <CTableDataCell className="text-end">
                            <CButton size="sm" color="secondary" variant="ghost" className="me-2" onClick={() => setModalData({ visible: true, data: { ...inc, start_time: new Date(inc.start_time), end_time: new Date(inc.end_time) } })}>
                              <CIcon icon={cilPencil} />
                            </CButton>
                            <CButton size="sm" color="danger" variant="ghost" onClick={() => handleDelete(inc.id)}>
                              <CIcon icon={cilTrash} />
                            </CButton>
                          </CTableDataCell>
                        )}
                      </CTableRow>
                    );
                  })
                )}
              </CTableBody>
            </CTable>
          </div>
        )}
      </CCard>

      <DowntimeModal 
        visible={modalData.visible} 
        onClose={() => setModalData({ visible: false, data: null })} 
        deviceId={deviceId} 
        initialData={modalData.data} 
        onSuccess={fetchIncidents} 
      />
    </div>
  );
};

export default DowntimesTab;